/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Fade, Modal, ModalProps, Typography } from '@mui/material';

import { Namespaces } from '../../i18n';
import { useHUModalStyles } from './HUModal2.styles';

export type HUModal2Props = ModalProps & {
  withTitleDivider?: boolean;
  children: React.ReactNode;
  header?: ((renderParams: { headerClass: string; titleClass: string }) => React.ReactNode) | React.ReactNode;
  footer?: ((renderParams: { footerClass: string }) => React.ReactNode) | React.ReactNode;
  size?: 'tiny' | 'small' | 'medium' | 'big' | 'large' | 'full-screen' | 'auto';
  onOk?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => any;
  onCancel?: () => any;
  okButtonText?: string;
  cancelButtonText?: string;
  title?: string | React.ReactNode;
  paperClassName?: string;
  okButtonProps?: React.ComponentProps<typeof Button>;
  cancelButtonProps?: React.ComponentProps<typeof Button>;
};

export function HUModal2(props: HUModal2Props) {
  const {
    withTitleDivider,
    children,
    footer,
    onOk,
    onCancel,
    okButtonText,
    cancelButtonText,
    title,
    size,
    paperClassName,
    open,
    header,
    okButtonProps = {},
    cancelButtonProps = {},
    ...otherProps
  } = props;
  const classes = useHUModalStyles();
  const { t } = useTranslation(Namespaces.Common);

  const renderHeader = () => {
    if (header === undefined) {
      if (title) {
        return (
          <div className={classes.header}>
            <Typography data-cy="modal-title" classes={{ h2: classes.title }} variant="h2">
              {title}
            </Typography>
          </div>
        );
      }
    } else if (typeof header === 'function') {
      return header({ headerClass: classes.header, titleClass: classes.title });
    }
    return header;
  };

  const renderFooter = () => {
    if (footer === undefined) {
      if (onOk || onCancel) {
        return (
          <div className={classes.footer}>
            {onOk && (
              <Button
                {...okButtonProps}
                data-testid="submit_modal"
                variant="contained"
                onClick={onOk}
                data-cy="modal-submit-button"
              >
                {okButtonText || t('label.ok')}
              </Button>
            )}
            {onCancel && (
              <Button {...cancelButtonProps} variant="text" onClick={onCancel} data-cy="modal-cancel-button">
                {cancelButtonText || t('label.cancel')}
              </Button>
            )}
          </div>
        );
      }
    } else if (typeof footer === 'function') {
      return footer({ footerClass: classes.footer });
    }
    return footer;
  };

  return (
    <Modal
      open={open}
      {...otherProps}
      className={cx(classes.modal, {
        [classes.titleDivider]: withTitleDivider,
      })}
    >
      <Fade in={open}>
        <div
          className={cx(classes.paper, paperClassName, {
            [classes.sizeTiny]: size === 'tiny',
            [classes.sizeSmall]: size === 'small',
            [classes.sizeMedium]: size === 'medium',
            [classes.sizeBig]: size === 'big',
            [classes.sizeLarge]: size === 'large',
            [classes.sizeFullScreen]: size === 'full-screen',
            [classes.sizeAuto]: size === 'auto',
          })}
        >
          {renderHeader()}
          <div className={classes.body} data-cy="modal-children">
            {children}
          </div>
          {renderFooter()}
        </div>
      </Fade>
    </Modal>
  );
}
